/** @format */

import {
	Box,
	Card,
	Chip,
	Divider,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Tab,
	Tabs,
	TextField,
	Alert,
	Paper,
	Collapse,
	LinearProgress,
	Avatar,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupIcon from "@mui/icons-material/Group";
import CycleIcon from "@mui/icons-material/Loop";
import EventIcon from "@mui/icons-material/Event";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import React, { useEffect, useState, useCallback } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { formatCurrency } from "../../../utility/currencyUtils";
import {
	getAllContributionsAsAdmin,
	getContributionsByDateAsAdmin,
	getFailedContributionsAsAdmin,
	getAllActiveCyclesAsAdmin,
	getCycleContributionDetailsAsAdmin,
	getCycleSessionsBreakdownAsAdmin,
	ContributionReportEntry,
	ActiveCycleInfo,
	CycleMemberContributionStatus,
	CycleSessionContributions,
} from "../../../redux/actions/adminContributionAction";
import { LoadingSpinner } from "../../allComponents/AllComponents";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";

// Tab types
type TabValue = "all" | "byDate" | "failed" | "activeCycles" | "cycleDetails";

// Custom day component for the calendar
const CustomPickersDay = (props: PickersDayProps<Dayjs>) => {
	const { day, selected, today, ...other } = props;

	return (
		<PickersDay
			{...other}
			day={day}
			selected={selected}
			today={today}
			sx={{
				...(selected && {
					backgroundColor: "primary.main",
					color: "primary.contrastText",
					"&:hover": {
						backgroundColor: "primary.dark",
					},
				}),
				...(today &&
					!selected && {
					border: "1px solid",
					borderColor: "primary.main",
				}),
			}}
		/>
	);
};

const ContributionReports = () => {
	const dispatch = useDispatch<AppDispatch>();

	// Selectors
	const adminContributions = useSelector(
		(state: RootState) => state.contribution.adminContributions,
	);
	const adminContributionsByDate = useSelector(
		(state: RootState) => state.contribution.adminContributionsByDate,
	);
	const adminFailedContributions = useSelector(
		(state: RootState) => state.contribution.adminFailedContributions,
	);
	const adminActiveCycles = useSelector(
		(state: RootState) => state.contribution.adminActiveCycles,
	);
	const adminCycleContributionDetails = useSelector(
		(state: RootState) => state.contribution.adminCycleContributionDetails,
	);
	const adminCycleSessionsBreakdown = useSelector(
		(state: RootState) => state.contribution.adminCycleSessionsBreakdown,
	);

	// UI State
	const [openNav, setOpenNav] = useState(false);
	const [activeTab, setActiveTab] = useState<TabValue>("all");
	const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
	const [searchQuery, setSearchQuery] = useState("");
	const [showCalendar, setShowCalendar] = useState(false);
	const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
	const [viewMode, setViewMode] = useState<"members" | "sessions">("members");

	// Pagination state for each tab
	const [allPage, setAllPage] = useState(0);
	const [allRowsPerPage, setAllRowsPerPage] = useState(10);
	const [byDatePage, setByDatePage] = useState(0);
	const [byDateRowsPerPage, setByDateRowsPerPage] = useState(10);
	const [failedPage, setFailedPage] = useState(0);
	const [failedRowsPerPage, setFailedRowsPerPage] = useState(10);
	const [cyclesPage, setCyclesPage] = useState(0);
	const [cyclesRowsPerPage, setCyclesRowsPerPage] = useState(10);

	// Expanded state
	const [expandedFailureAnalysis, setExpandedFailureAnalysis] = useState(false);

	// Dismissed error state
	const [dismissedError, setDismissedError] = useState(false);

	// Get user's timezone
	const userTimezone =
		Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";

	// Determine loading state
	const isLoading =
		adminContributions?.isLoading ||
		adminContributionsByDate?.isLoading ||
		adminFailedContributions?.isLoading ||
		adminActiveCycles?.isLoading ||
		adminCycleContributionDetails?.isLoading ||
		adminCycleSessionsBreakdown?.isLoading;

	// Determine error
	const error =
		adminContributions?.error ||
		adminContributionsByDate?.error ||
		adminFailedContributions?.error ||
		adminActiveCycles?.error ||
		adminCycleContributionDetails?.error ||
		adminCycleSessionsBreakdown?.error;

	// Fetch data functions
	const fetchAllContributions = useCallback(() => {
		dispatch(
			getAllContributionsAsAdmin({
				page: allPage + 1,
				limit: allRowsPerPage,
			}),
		);
	}, [dispatch, allPage, allRowsPerPage]);

	const fetchContributionsByDate = useCallback(() => {
		const dateStr = selectedDate.format("YYYY-MM-DD");
		dispatch(
			getContributionsByDateAsAdmin({
				date: dateStr,
				page: byDatePage + 1,
				limit: byDateRowsPerPage,
				timezone: userTimezone,
			}),
		);
	}, [dispatch, selectedDate, byDatePage, byDateRowsPerPage, userTimezone]);

	const fetchFailedContributions = useCallback(() => {
		dispatch(
			getFailedContributionsAsAdmin({
				page: failedPage + 1,
				limit: failedRowsPerPage,
			}),
		);
	}, [dispatch, failedPage, failedRowsPerPage]);

	const fetchActiveCycles = useCallback(() => {
		dispatch(
			getAllActiveCyclesAsAdmin({
				page: cyclesPage + 1,
				limit: cyclesRowsPerPage,
			}),
		);
	}, [dispatch, cyclesPage, cyclesRowsPerPage]);

	const fetchCycleDetails = useCallback(
		(cycleId: string) => {
			dispatch(getCycleContributionDetailsAsAdmin(cycleId));
			dispatch(getCycleSessionsBreakdownAsAdmin(cycleId));
		},
		[dispatch],
	);

	useEffect(() => {
		if (activeTab === "all") {
			fetchAllContributions();
		} else if (activeTab === "byDate") {
			fetchContributionsByDate();
		} else if (activeTab === "failed") {
			fetchFailedContributions();
		} else if (activeTab === "activeCycles") {
			fetchActiveCycles();
		} else if (activeTab === "cycleDetails" && selectedCycleId) {
			fetchCycleDetails(selectedCycleId);
		}
	}, [
		activeTab,
		fetchAllContributions,
		fetchContributionsByDate,
		fetchFailedContributions,
		fetchActiveCycles,
		fetchCycleDetails,
		selectedCycleId,
	]);

	const handleTabChange = (
		_event: React.SyntheticEvent,
		newValue: TabValue,
	) => {
		setActiveTab(newValue);
		setSearchQuery("");
	};

	const handleDateChange = (newDate: Dayjs | null) => {
		if (newDate) {
			setSelectedDate(newDate);
			setByDatePage(0);
		}
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
	};

	const handleRefresh = () => {
		setDismissedError(false); // Reset dismissed error on refresh
		if (activeTab === "all") {
			fetchAllContributions();
		} else if (activeTab === "byDate") {
			fetchContributionsByDate();
		} else if (activeTab === "failed") {
			fetchFailedContributions();
		} else if (activeTab === "activeCycles") {
			fetchActiveCycles();
		} else if (activeTab === "cycleDetails" && selectedCycleId) {
			fetchCycleDetails(selectedCycleId);
		}
	};

	const handleCycleClick = (cycleId: string) => {
		setSelectedCycleId(cycleId);
		setActiveTab("cycleDetails");
	};

	// Filter contributions based on search query
	const filterContributions = (
		contributions: ContributionReportEntry[],
	): ContributionReportEntry[] => {
		if (!searchQuery) return contributions;
		const query = searchQuery.toLowerCase();
		return contributions.filter((contrib: any) => {
			// Handle both nested and flat response structures
			const memberName = (
				contrib.member?.name ||
				contrib.memberName ||
				""
			).toLowerCase();
			const memberEmail = (
				contrib.member?.email ||
				contrib.memberEmail ||
				""
			).toLowerCase();
			const circleName = (
				contrib.circle?.name ||
				contrib.circleName ||
				""
			).toLowerCase();
			return (
				memberName.includes(query) ||
				memberEmail.includes(query) ||
				circleName.includes(query)
			);
		});
	};

	// Status chip helper
	const getStatusChip = (status: string) => {
		switch (status?.toLowerCase()) {
			case "completed":
				return (
					<Chip
						label="Completed"
						color="success"
						size="small"
						icon={<CheckCircleIcon />}
					/>
				);
			case "failed":
				return (
					<Chip
						label="Failed"
						color="error"
						size="small"
						icon={<ErrorIcon />}
					/>
				);
			case "pending":
				return (
					<Chip
						label="Pending"
						color="warning"
						size="small"
						icon={<PendingIcon />}
					/>
				);
			default:
				return <Chip label={status || "Unknown"} size="small" />;
		}
	};

	// Member contribution status chip
	const getMemberStatusChip = (status: string) => {
		switch (status) {
			case "up-to-date":
				return (
					<Chip
						label="Up to Date"
						color="success"
						size="small"
						icon={<CheckCircleIcon />}
					/>
				);
			case "behind":
				return (
					<Chip
						label="Behind"
						color="warning"
						size="small"
						icon={<AccessTimeIcon />}
					/>
				);
			case "no-contributions":
				return (
					<Chip
						label="No Contributions"
						color="error"
						size="small"
						icon={<ErrorIcon />}
					/>
				);
			default:
				return <Chip label={status} size="small" />;
		}
	};

	// Render All Contributions Tab
	const renderAllTab = () => {
		const contributions = adminContributions?.contributions || [];
		const pagination = adminContributions?.pagination;
		const summary = adminContributions?.summary;

		const filteredContributions = filterContributions(contributions);

		return (
			<Stack spacing={3}>
				{/* Summary Cards */}
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "primary.lighter",
									}}>
									<TrendingUpIcon color="primary" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold">
										{summary?.totalContributions || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Contributions
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "success.lighter",
									}}>
									<AttachMoneyIcon color="success" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="success.main">
										{summary?.totalAmountFormatted || "$0.00"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Amount
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "success.lighter",
									}}>
									<CheckCircleIcon color="success" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="success.main">
										{summary?.completedCount || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Completed
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "error.lighter",
									}}>
									<ErrorIcon color="error" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold" color="error.main">
										{summary?.failedCount || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Failed
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{/* Contributions Table */}
				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "900px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Amount</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Contribution Date</TableCell>
									<TableCell>Created At</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredContributions.length === 0 ?
									<TableRow>
										<TableCell colSpan={7} align="center">
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ py: 4 }}>
												No contributions found
											</Typography>
										</TableCell>
									</TableRow>
									: filteredContributions.map((contrib: any, index) => (
										<TableRow hover key={contrib.contributionId || index}>
											<TableCell>
												<Typography variant="subtitle2">
													{contrib.member?.name || contrib.memberName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{contrib.member?.email ||
														contrib.memberEmail ||
														"N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{contrib.circle?.name || contrib.circleName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="medium">
													{contrib.amount?.formatted ||
														contrib.amountFormatted ||
														"N/A"}
												</Typography>
											</TableCell>
											<TableCell>{getStatusChip(contrib.status)}</TableCell>
											<TableCell>
												<Typography variant="body2">
													{contrib.contributionForDate || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{dayjs(
														contrib.contributedAt || contrib.createdAt,
													).format("MMM D, YYYY h:mm A")}
												</Typography>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={pagination?.total || filteredContributions.length}
						onPageChange={(_, newPage) => setAllPage(newPage)}
						onRowsPerPageChange={(e) => {
							setAllRowsPerPage(parseInt(e.target.value, 10));
							setAllPage(0);
						}}
						page={allPage}
						rowsPerPage={allRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render By Date Tab
	const renderByDateTab = () => {
		const contributions = adminContributionsByDate?.contributions || [];
		const pagination = adminContributionsByDate?.pagination;
		const summary = adminContributionsByDate?.summary;

		const filteredContributions = filterContributions(contributions);

		return (
			<Stack spacing={3}>
				{/* Date Picker */}
				<Card sx={{ p: 2 }}>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} md={6}>
							<Stack direction="row" spacing={1} alignItems="center">
								<CalendarMonthIcon color="primary" />
								<TextField
									label="Selected Date"
									value={selectedDate.format("YYYY-MM-DD")}
									size="small"
									InputProps={{
										readOnly: true,
									}}
									onClick={() => setShowCalendar(!showCalendar)}
									sx={{ cursor: "pointer" }}
								/>
								<IconButton
									onClick={() => setShowCalendar(!showCalendar)}
									color={showCalendar ? "primary" : "default"}>
									<CalendarMonthIcon />
								</IconButton>
							</Stack>
						</Grid>
						<Grid item xs={12} md={6}>
							<Stack direction="row" spacing={1} alignItems="center">
								<InfoOutlinedIcon color="action" fontSize="small" />
								<Typography variant="body2" color="text.secondary">
									Showing contributions for:{" "}
									{selectedDate.format("MMMM D, YYYY")}
								</Typography>
							</Stack>
						</Grid>
					</Grid>

					{/* Calendar Dropdown */}
					<Collapse in={showCalendar}>
						<Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
							<Paper elevation={3} sx={{ borderRadius: 2 }}>
								<DateCalendar
									value={selectedDate}
									onChange={handleDateChange}
									slots={{
										day: CustomPickersDay,
									}}
									maxDate={dayjs()}
								/>
							</Paper>
						</Box>
					</Collapse>
				</Card>

				{/* Summary Cards */}
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "primary.lighter",
									}}>
									<TrendingUpIcon color="primary" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold">
										{summary?.totalContributions || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Contributions on {selectedDate.format("MMM D")}
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "success.lighter",
									}}>
									<AttachMoneyIcon color="success" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="success.main">
										{summary?.totalAmountFormatted || "$0.00"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Collected
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "info.lighter",
									}}>
									<AttachMoneyIcon color="info" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold" color="info.main">
										{formatCurrency(summary?.averageAmount || 0, "usd")}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Average Amount
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{/* Contributions Table */}
				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "800px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Amount</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Time</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredContributions.length === 0 ?
									<TableRow>
										<TableCell colSpan={6} align="center">
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ py: 4 }}>
												No contributions found for{" "}
												{selectedDate.format("MMMM D, YYYY")}
											</Typography>
										</TableCell>
									</TableRow>
									: filteredContributions.map((contrib: any, index) => (
										<TableRow hover key={contrib.contributionId || index}>
											<TableCell>
												<Typography variant="subtitle2">
													{contrib.member?.name || contrib.memberName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{contrib.member?.email ||
														contrib.memberEmail ||
														"N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{contrib.circle?.name || contrib.circleName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="medium">
													{contrib.amount?.formatted ||
														contrib.amountFormatted ||
														"N/A"}
												</Typography>
											</TableCell>
											<TableCell>{getStatusChip(contrib.status)}</TableCell>
											<TableCell>
												<Typography variant="body2">
													{dayjs(
														contrib.contributedAt || contrib.createdAt,
													).format("h:mm A")}
												</Typography>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={pagination?.total || filteredContributions.length}
						onPageChange={(_, newPage) => setByDatePage(newPage)}
						onRowsPerPageChange={(e) => {
							setByDateRowsPerPage(parseInt(e.target.value, 10));
							setByDatePage(0);
						}}
						page={byDatePage}
						rowsPerPage={byDateRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render Failed Tab
	const renderFailedTab = () => {
		const contributions = adminFailedContributions.contributions || [];
		const pagination = adminFailedContributions.pagination;
		const failureAnalysis = adminFailedContributions.failureAnalysis;

		const filteredContributions = filterContributions(contributions);

		return (
			<Stack spacing={3}>
				{/* Failure Analysis Card */}
				{failureAnalysis && (
					<Card>
						<Box
							sx={{
								p: 2,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								cursor: "pointer",
							}}
							onClick={() =>
								setExpandedFailureAnalysis(!expandedFailureAnalysis)
							}>
							<Stack direction="row" spacing={1} alignItems="center">
								<ErrorIcon color="error" />
								<Typography variant="h6">Failure Analysis</Typography>
							</Stack>
							<IconButton size="small">
								{expandedFailureAnalysis ?
									<ExpandLessIcon />
									: <ExpandMoreIcon />}
							</IconButton>
						</Box>
						<Collapse in={expandedFailureAnalysis}>
							<Divider />
							<Box sx={{ p: 2 }}>
								<Grid container spacing={3}>
									<Grid item xs={12} md={6}>
										<Typography variant="subtitle2" gutterBottom>
											By Status
										</Typography>
										<Stack spacing={1}>
											{Object.entries(failureAnalysis.byStatus || {}).map(
												([status, count]) => (
													<Stack
														key={status}
														direction="row"
														justifyContent="space-between"
														alignItems="center">
														<Typography variant="body2" color="text.secondary">
															{status}
														</Typography>
														<Chip
															label={count as number}
															size="small"
															color="error"
														/>
													</Stack>
												),
											)}
										</Stack>
									</Grid>
									<Grid item xs={12} md={6}>
										<Typography variant="subtitle2" gutterBottom>
											By Circle
										</Typography>
										<Stack spacing={1}>
											{Object.entries(failureAnalysis.byCircle || {})
												.slice(0, 5)
												.map(([circle, count]) => (
													<Stack
														key={circle}
														direction="row"
														justifyContent="space-between"
														alignItems="center">
														<Typography variant="body2" color="text.secondary">
															{circle}
														</Typography>
														<Chip
															label={count as number}
															size="small"
															variant="outlined"
														/>
													</Stack>
												))}
										</Stack>
									</Grid>
								</Grid>
							</Box>
						</Collapse>
					</Card>
				)}

				{/* Failed Contributions Table */}
				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "800px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Amount</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Contribution Date</TableCell>
									<TableCell>Created At</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredContributions.length === 0 ?
									<TableRow>
										<TableCell colSpan={7} align="center">
											<Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
												<CheckCircleIcon
													sx={{ fontSize: 48, color: "success.main" }}
												/>
												<Typography variant="body2" color="text.secondary">
													No failed contributions found
												</Typography>
											</Stack>
										</TableCell>
									</TableRow>
									: filteredContributions.map((contrib: any, index) => (
										<TableRow hover key={contrib.contributionId || index}>
											<TableCell>
												<Typography variant="subtitle2">
													{contrib.member?.name || contrib.memberName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{contrib.member?.email ||
														contrib.memberEmail ||
														"N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{contrib.circle?.name || contrib.circleName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="medium">
													{contrib.amount?.formatted ||
														contrib.amountFormatted ||
														"N/A"}
												</Typography>
											</TableCell>
											<TableCell>{getStatusChip(contrib.status)}</TableCell>
											<TableCell>
												<Typography variant="body2">
													{contrib.contributionForDate || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{dayjs(
														contrib.contributedAt || contrib.createdAt,
													).format("MMM D, YYYY h:mm A")}
												</Typography>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={pagination?.total || filteredContributions.length}
						onPageChange={(_, newPage) => setFailedPage(newPage)}
						onRowsPerPageChange={(e) => {
							setFailedRowsPerPage(parseInt(e.target.value, 10));
							setFailedPage(0);
						}}
						page={failedPage}
						rowsPerPage={failedRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render Active Cycles Tab
	const renderActiveCyclesTab = () => {
		const cycles = adminActiveCycles.cycles || [];
		const pagination = adminActiveCycles.pagination;
		const summary = adminActiveCycles.summary;

		return (
			<Stack spacing={3}>
				{/* Summary Cards */}
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "primary.lighter",
									}}>
									<CycleIcon color="primary" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold">
										{summary?.totalActiveCycles || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Active Cycles
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "info.lighter",
									}}>
									<AttachMoneyIcon color="info" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold" color="info.main">
										{formatCurrency(summary?.totalExpectedContributions || 0, "usd")}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Expected Total
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "success.lighter",
									}}>
									<AttachMoneyIcon color="success" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="success.main">
										{formatCurrency(summary?.totalActualContributions || 0, "usd")}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Actual Collected
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{/* Active Cycles Grid */}
				<Grid container spacing={2}>
					{cycles.length === 0 ?
						<Grid item xs={12}>
							<Card sx={{ p: 4, textAlign: "center" }}>
								<Typography variant="body2" color="text.secondary">
									No active cycles found
								</Typography>
							</Card>
						</Grid>
						: cycles.map((cycle: ActiveCycleInfo) => (
							<Grid item xs={12} md={6} lg={4} key={cycle.cycleId}>
								<Card
									sx={{
										p: 2,
										cursor: "pointer",
										transition: "all 0.2s",
										"&:hover": {
											boxShadow: 4,
											transform: "translateY(-2px)",
										},
									}}
									onClick={() => handleCycleClick(cycle.cycleId)}>
									<Stack spacing={2}>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center">
											<Typography variant="h6" noWrap sx={{ maxWidth: "70%" }}>
												{cycle.circleName}
											</Typography>
											<Chip label={cycle.status} color="success" size="small" />
										</Stack>

										<Divider />

										<Grid container spacing={1}>
											<Grid item xs={6}>
												<Typography
													variant="caption"
													color="text.secondary"
													display="block">
													Start Date
												</Typography>
												<Typography variant="body2">
													{dayjs(cycle.startDate).format("MMM D, YYYY")}
												</Typography>
											</Grid>
											<Grid item xs={6}>
												<Typography
													variant="caption"
													color="text.secondary"
													display="block">
													End Date
												</Typography>
												<Typography variant="body2">
													{dayjs(cycle.endDate).format("MMM D, YYYY")}
												</Typography>
											</Grid>
										</Grid>

										{/* Upcoming Payout Info */}
										{cycle.upcomingPayoutDate && (
											<Box
												sx={{
													p: 1.5,
													borderRadius: 1,
													bgcolor: "warning.lighter",
													border: "1px solid",
													borderColor: "warning.light",
												}}>
												<Stack spacing={0.5}>
													<Stack
														direction="row"
														justifyContent="space-between"
														alignItems="center">
														<Typography
															variant="caption"
															color="warning.dark"
															fontWeight="bold">
															Upcoming Payout (Session{" "}
															{cycle.currentSessionIndex}/{cycle.totalSessions})
														</Typography>
													</Stack>
													<Typography variant="body2" fontWeight="medium">
														{dayjs(cycle.upcomingPayoutDate).format(
															"MMM D, YYYY",
														)}
													</Typography>
													{cycle.upcomingPayoutRecipientName && (
														<Typography
															variant="caption"
															color="text.secondary">
															Recipient: {cycle.upcomingPayoutRecipientName}
														</Typography>
													)}
												</Stack>
											</Box>
										)}

										<Stack direction="row" spacing={2} alignItems="center">
											<GroupIcon fontSize="small" color="action" />
											<Typography variant="body2">
												{cycle.memberCount} members
											</Typography>
											<Divider orientation="vertical" flexItem />
											<Typography variant="body2" color="success.main">
												{cycle.contributionsTotalFormatted}
											</Typography>
										</Stack>

										{/* Progress Bar */}
										<Box>
											<Stack
												direction="row"
												justifyContent="space-between"
												mb={0.5}>
												<Typography variant="caption" color="text.secondary">
													Cycle Progress
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{cycle.daysRemaining} days remaining
												</Typography>
											</Stack>
											<LinearProgress
												variant="determinate"
												value={cycle.progressPercentage}
												sx={{ height: 6, borderRadius: 1 }}
											/>
										</Box>

										<Stack direction="row" spacing={1} flexWrap="wrap">
											<Chip
												label={cycle.contributionFrequency}
												size="small"
												variant="outlined"
											/>
											{cycle.contributionDays?.slice(0, 2).map((day) => (
												<Chip
													key={day}
													label={day}
													size="small"
													variant="outlined"
													color="primary"
												/>
											))}
										</Stack>
									</Stack>
								</Card>
							</Grid>
						))
					}
				</Grid>

				{pagination && pagination.pages > 1 && (
					<TablePagination
						component="div"
						count={pagination.total}
						onPageChange={(_, newPage) => setCyclesPage(newPage)}
						onRowsPerPageChange={(e) => {
							setCyclesRowsPerPage(parseInt(e.target.value, 10));
							setCyclesPage(0);
						}}
						page={cyclesPage}
						rowsPerPage={cyclesRowsPerPage}
						rowsPerPageOptions={[6, 12, 24]}
					/>
				)}
			</Stack>
		);
	};

	// Render Cycle Details Tab
	const renderCycleDetailsTab = () => {
		const cycle = adminCycleContributionDetails.cycle;
		const memberContributions =
			adminCycleContributionDetails.memberContributions || [];
		const memberSummary = adminCycleContributionDetails.summary;
		const sessions = adminCycleSessionsBreakdown.sessions || [];
		const sessionsSummary = adminCycleSessionsBreakdown.summary;

		if (!cycle) {
			return (
				<Card sx={{ p: 4, textAlign: "center" }}>
					<Typography variant="body2" color="text.secondary">
						Select a cycle to view details
					</Typography>
				</Card>
			);
		}

		return (
			<Stack spacing={3}>
				{/* Back Button & Cycle Header */}
				<Card sx={{ p: 2 }}>
					<Stack spacing={2}>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center">
							<Stack direction="row" spacing={2} alignItems="center">
								<IconButton
									onClick={() => setActiveTab("activeCycles")}
									size="small">
									<ExpandLessIcon sx={{ transform: "rotate(-90deg)" }} />
								</IconButton>
								<Typography variant="h5">{cycle.circleName}</Typography>
								<Chip label={cycle.status} color="success" size="small" />
							</Stack>
							<Stack direction="row" spacing={1}>
								<Chip
									label="Members View"
									color={viewMode === "members" ? "primary" : "default"}
									onClick={() => setViewMode("members")}
									clickable
								/>
								<Chip
									label="Sessions View"
									color={viewMode === "sessions" ? "primary" : "default"}
									onClick={() => setViewMode("sessions")}
									clickable
								/>
							</Stack>
						</Stack>

						<Grid container spacing={2}>
							<Grid item xs={6} sm={3}>
								<Typography variant="caption" color="text.secondary">
									Start Date
								</Typography>
								<Typography variant="body2">
									{dayjs(cycle.startDate).format("MMM D, YYYY")}
								</Typography>
							</Grid>
							<Grid item xs={6} sm={3}>
								<Typography variant="caption" color="text.secondary">
									End Date
								</Typography>
								<Typography variant="body2">
									{dayjs(cycle.endDate).format("MMM D, YYYY")}
								</Typography>
							</Grid>
							<Grid item xs={6} sm={3}>
								<Typography variant="caption" color="text.secondary">
									Upcoming Payout
									{cycle.currentSessionIndex && cycle.totalSessions ?
										` (${cycle.currentSessionIndex}/${cycle.totalSessions})`
										: ""}
								</Typography>
								<Typography
									variant="body2"
									color="warning.main"
									fontWeight="medium">
									{cycle.upcomingPayoutDate ?
										dayjs(cycle.upcomingPayoutDate).format("MMM D, YYYY")
										: "Completed"}
								</Typography>
								{cycle.upcomingPayoutRecipientName && (
									<Typography variant="caption" color="text.secondary">
										→ {cycle.upcomingPayoutRecipientName}
									</Typography>
								)}
							</Grid>
							<Grid item xs={6} sm={3}>
								<Typography variant="caption" color="text.secondary">
									Days Remaining
								</Typography>
								<Typography variant="body2">{cycle.daysRemaining}</Typography>
							</Grid>
						</Grid>

						{/* Progress Bar */}
						<Box>
							<LinearProgress
								variant="determinate"
								value={cycle.progressPercentage}
								sx={{ height: 8, borderRadius: 1 }}
							/>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5 }}>
								{cycle.progressPercentage}% complete
							</Typography>
						</Box>
					</Stack>
				</Card>

				{viewMode === "members" ?
					<>
						{/* Member Summary Cards */}
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6} md={3}>
								<Card sx={{ p: 2 }}>
									<Stack direction="row" spacing={2} alignItems="center">
										<GroupIcon color="primary" />
										<Box>
											<Typography variant="h5">
												{memberSummary?.totalMembers || 0}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Total Members
											</Typography>
										</Box>
									</Stack>
								</Card>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<Card sx={{ p: 2 }}>
									<Stack direction="row" spacing={2} alignItems="center">
										<CheckCircleIcon color="success" />
										<Box>
											<Typography variant="h5" color="success.main">
												{memberSummary?.membersContributed || 0}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Contributed
											</Typography>
										</Box>
									</Stack>
								</Card>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<Card sx={{ p: 2 }}>
									<Stack direction="row" spacing={2} alignItems="center">
										<ErrorIcon color="error" />
										<Box>
											<Typography variant="h5" color="error.main">
												{memberSummary?.membersNotContributed || 0}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Not Contributed
											</Typography>
										</Box>
									</Stack>
								</Card>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<Card sx={{ p: 2 }}>
									<Stack direction="row" spacing={2} alignItems="center">
										<AttachMoneyIcon color="success" />
										<Box>
											<Typography variant="h5" color="success.main">
												{memberSummary?.totalContributionsAmountFormatted ||
													"$0.00"}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Total Collected
											</Typography>
										</Box>
									</Stack>
								</Card>
							</Grid>
						</Grid>

						{/* Members Table */}
						<Card>
							<Box sx={{ overflowX: "auto" }}>
								<Table sx={{ minWidth: "800px" }}>
									<TableHead>
										<TableRow>
											<TableCell>Member</TableCell>
											<TableCell>Email</TableCell>
											<TableCell>Role</TableCell>
											<TableCell>Contributions</TableCell>
											<TableCell>Total Amount</TableCell>
											<TableCell>Last Contribution</TableCell>
											<TableCell>Status</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{memberContributions.length === 0 ?
											<TableRow>
												<TableCell colSpan={7} align="center">
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{ py: 4 }}>
														No member data available
													</Typography>
												</TableCell>
											</TableRow>
											: memberContributions.map(
												(member: CycleMemberContributionStatus) => (
													<TableRow hover key={member.userId}>
														<TableCell>
															<Stack
																direction="row"
																spacing={1}
																alignItems="center">
																<Avatar sx={{ width: 32, height: 32 }}>
																	{member.name?.charAt(0) || "?"}
																</Avatar>
																<Typography variant="subtitle2">
																	{member.name || "N/A"}
																</Typography>
															</Stack>
														</TableCell>
														<TableCell>
															<Typography
																variant="body2"
																color="text.secondary">
																{member.email || "N/A"}
															</Typography>
														</TableCell>
														<TableCell>
															{member.isLeader ?
																<Chip
																	label="Leader"
																	color="primary"
																	size="small"
																/>
																: <Chip
																	label="Member"
																	variant="outlined"
																	size="small"
																/>
															}
														</TableCell>
														<TableCell>
															<Chip
																label={member.contributionCount}
																size="small"
																color={
																	member.contributionCount > 0 ?
																		"success"
																		: "default"
																}
															/>
														</TableCell>
														<TableCell>
															<Typography variant="body2" fontWeight="medium">
																{member.totalContributedFormatted}
															</Typography>
														</TableCell>
														<TableCell>
															<Typography variant="body2">
																{member.lastContributionDate ?
																	dayjs(member.lastContributionDate).format(
																		"MMM D, YYYY",
																	)
																	: "Never"}
															</Typography>
														</TableCell>
														<TableCell>
															{getMemberStatusChip(member.status)}
														</TableCell>
													</TableRow>
												),
											)
										}
									</TableBody>
								</Table>
							</Box>
						</Card>
					</>
					: <>
						{/* Sessions Summary Cards */}
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6} md={3}>
								<Card sx={{ p: 2 }}>
									<Stack direction="row" spacing={2} alignItems="center">
										<EventIcon color="primary" />
										<Box>
											<Typography variant="h5">
												{sessionsSummary?.totalSessions || 0}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Total Sessions
											</Typography>
										</Box>
									</Stack>
								</Card>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<Card sx={{ p: 2 }}>
									<Stack direction="row" spacing={2} alignItems="center">
										<CheckCircleIcon color="success" />
										<Box>
											<Typography variant="h5" color="success.main">
												{sessionsSummary?.completedSessions || 0}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Completed
											</Typography>
										</Box>
									</Stack>
								</Card>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<Card sx={{ p: 2 }}>
									<Stack direction="row" spacing={2} alignItems="center">
										<AccessTimeIcon color="info" />
										<Box>
											<Typography variant="h5" color="info.main">
												{sessionsSummary?.upcomingSessions || 0}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Upcoming
											</Typography>
										</Box>
									</Stack>
								</Card>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<Card sx={{ p: 2 }}>
									<Stack direction="row" spacing={2} alignItems="center">
										<TrendingUpIcon color="success" />
										<Box>
											<Typography variant="h5" color="success.main">
												{sessionsSummary?.overallCompletionPercentage || 0}%
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Completion Rate
											</Typography>
										</Box>
									</Stack>
								</Card>
							</Grid>
						</Grid>

						{/* Sessions Cards */}
						<Grid container spacing={2}>
							{sessions.length === 0 ?
								<Grid item xs={12}>
									<Card sx={{ p: 4, textAlign: "center" }}>
										<Typography variant="body2" color="text.secondary">
											No session data available
										</Typography>
									</Card>
								</Grid>
								: sessions.map((session: CycleSessionContributions) => (
									<Grid item xs={12} md={6} key={session.sessionDate}>
										<Card
											sx={{
												p: 2,
												borderLeft: 4,
												borderColor:
													session.isToday ? "primary.main"
														: session.isPast ? "success.main"
															: "grey.300",
											}}>
											<Stack spacing={2}>
												<Stack
													direction="row"
													justifyContent="space-between"
													alignItems="center">
													<Stack>
														<Typography variant="subtitle1" fontWeight="bold">
															{session.dayOfWeek}
														</Typography>
														<Typography variant="body2" color="text.secondary">
															{session.sessionDateFormatted}
														</Typography>
													</Stack>
													<Stack direction="row" spacing={1}>
														{session.isToday && (
															<Chip
																label="Today"
																color="primary"
																size="small"
															/>
														)}
														{session.isPast && !session.isToday && (
															<Chip
																label="Past"
																color="success"
																size="small"
																variant="outlined"
															/>
														)}
														{session.isFuture && (
															<Chip
																label="Upcoming"
																color="default"
																size="small"
																variant="outlined"
															/>
														)}
													</Stack>
												</Stack>

												<Grid container spacing={2}>
													<Grid item xs={6}>
														<Typography
															variant="caption"
															color="text.secondary">
															Contributed
														</Typography>
														<Typography variant="h6" color="success.main">
															{session.membersContributed} /{" "}
															{session.membersContributed +
																session.membersNotContributed}
														</Typography>
													</Grid>
													<Grid item xs={6}>
														<Typography
															variant="caption"
															color="text.secondary">
															Collected
														</Typography>
														<Typography variant="h6">
															{session.totalCollectedFormatted}
														</Typography>
													</Grid>
												</Grid>

												{/* Completion Progress */}
												<Box>
													<Stack
														direction="row"
														justifyContent="space-between"
														mb={0.5}>
														<Typography
															variant="caption"
															color="text.secondary">
															Completion
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary">
															{session.completionPercentage}%
														</Typography>
													</Stack>
													<LinearProgress
														variant="determinate"
														value={session.completionPercentage}
														color={
															session.completionPercentage === 100 ? "success"
																: session.completionPercentage >= 50 ?
																	"primary"
																	: "warning"
														}
														sx={{ height: 6, borderRadius: 1 }}
													/>
												</Box>

												{/* Member Avatars */}
												{session.members && session.members.length > 0 && (
													<Stack direction="row" spacing={-1}>
														{session.members.slice(0, 5).map((m) => (
															<Tooltip
																key={m.userId}
																title={`${m.name}: ${m.contributed ? m.contributionAmountFormatted : "Not contributed"}`}>
																<Avatar
																	sx={{
																		width: 28,
																		height: 28,
																		fontSize: 12,
																		border: 2,
																		borderColor:
																			m.contributed ? "success.main" : (
																				"grey.300"
																			),
																		bgcolor:
																			m.contributed ? "success.lighter" : (
																				"grey.100"
																			),
																		color:
																			m.contributed ? "success.main" : (
																				"text.secondary"
																			),
																	}}>
																	{m.name?.charAt(0) || "?"}
																</Avatar>
															</Tooltip>
														))}
														{session.members.length > 5 && (
															<Avatar
																sx={{
																	width: 28,
																	height: 28,
																	fontSize: 10,
																	bgcolor: "grey.200",
																}}>
																+{session.members.length - 5}
															</Avatar>
														)}
													</Stack>
												)}
											</Stack>
										</Card>
									</Grid>
								))
							}
						</Grid>
					</>
				}
			</Stack>
		);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<React.Fragment>
				<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
				<Box
					sx={{
						minHeight: 1,
						display: "flex",
						flexDirection: { xs: "column", lg: "row" },
					}}>
					<DrawerNavbar
						openNav={openNav}
						onCloseNav={() => setOpenNav(false)}
					/>

					<Main>
						{(
							isLoading &&
							!adminContributions.contributions.length &&
							!adminActiveCycles.cycles.length
						) ?
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: "60vh",
								}}>
								<LoadingSpinner loading={isLoading} size={45} />
							</Box>
							: <Stack spacing={3}>
								{/* Header */}
								<Stack direction="row" spacing={3}>
									<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
										<Typography variant="h4">Contribution Reports</Typography>
										<Typography color="text.secondary" variant="body2">
											View and analyze contribution data across all circles and
											cycles
										</Typography>
									</Stack>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Tooltip title="Refresh">
											<span>
												<IconButton
													onClick={handleRefresh}
													disabled={isLoading}>
													{isLoading ?
														<CircularProgress size={24} />
														: <ArrowsClockwise />}
												</IconButton>
											</span>
										</Tooltip>
									</Box>
								</Stack>

								{/* Error Alert */}
								{error && !dismissedError && (
									<Alert
										severity="error"
										onClose={() => setDismissedError(true)}>
										{error}
									</Alert>
								)}

								{/* Search */}
								<Card sx={{ p: 2 }}>
									<OutlinedInput
										value={searchQuery}
										onChange={handleSearchChange}
										fullWidth
										size="small"
										placeholder="Search by member name, email, or circle..."
										startAdornment={
											<InputAdornment position="start">
												<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
											</InputAdornment>
										}
									/>
								</Card>

								{/* Tabs */}
								<Card>
									<Tabs
										value={activeTab}
										onChange={handleTabChange}
										indicatorColor="primary"
										textColor="primary"
										variant="scrollable"
										scrollButtons="auto">
										<Tab
											label={
												<Stack direction="row" spacing={1} alignItems="center">
													<TrendingUpIcon />
													<span>All</span>
													{adminContributions.pagination?.total !==
														undefined && (
															<Chip
																label={adminContributions.pagination.total}
																size="small"
																color="primary"
															/>
														)}
												</Stack>
											}
											value="all"
										/>
										<Tab
											label={
												<Stack direction="row" spacing={1} alignItems="center">
													<CalendarMonthIcon />
													<span>By Date</span>
												</Stack>
											}
											value="byDate"
										/>
										<Tab
											label={
												<Stack direction="row" spacing={1} alignItems="center">
													<ErrorIcon />
													<span>Failed</span>
													{adminFailedContributions.pagination?.total !==
														undefined && (
															<Chip
																label={adminFailedContributions.pagination.total}
																size="small"
																color="error"
															/>
														)}
												</Stack>
											}
											value="failed"
										/>
										<Tab
											label={
												<Stack direction="row" spacing={1} alignItems="center">
													<CycleIcon />
													<span>Active Cycles</span>
													{adminActiveCycles.pagination?.total !==
														undefined && (
															<Chip
																label={adminActiveCycles.pagination.total}
																size="small"
																color="success"
															/>
														)}
												</Stack>
											}
											value="activeCycles"
										/>
										{selectedCycleId && (
											<Tab
												label={
													<Stack
														direction="row"
														spacing={1}
														alignItems="center">
														<InfoOutlinedIcon />
														<span>Cycle Details</span>
													</Stack>
												}
												value="cycleDetails"
											/>
										)}
									</Tabs>
								</Card>

								{/* Tab Content */}
								{isLoading ?
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											minHeight: 300,
										}}>
										<CircularProgress />
									</Box>
									: <>
										{activeTab === "all" && renderAllTab()}
										{activeTab === "byDate" && renderByDateTab()}
										{activeTab === "failed" && renderFailedTab()}
										{activeTab === "activeCycles" && renderActiveCyclesTab()}
										{activeTab === "cycleDetails" && renderCycleDetailsTab()}
									</>
								}
							</Stack>
						}
					</Main>
				</Box>
			</React.Fragment>
		</LocalizationProvider>
	);
};

export default ContributionReports;
